import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import { el } from "./lib";

// PDF.js 与 worker 同版本、同源托管；只在打开绘本时加载。
type PdfLibrary = typeof import("pdfjs-dist");
let library: Promise<PdfLibrary> | undefined;
function loadPdfLibrary(): Promise<PdfLibrary> {
  if (!library) {
    const moduleUrl = "/assets/pdfjs/pdf.mjs";
    library = import(moduleUrl).then((pdfjs: PdfLibrary) => {
      pdfjs.GlobalWorkerOptions.workerSrc = `/assets/pdfjs/pdf.worker.mjs?v=${pdfjs.version}`;
      return pdfjs;
    }).catch(err => { library = undefined; throw err; });
  }
  return library;
}

export type PdfPageChangeSource = "initial" | "user" | "programmatic" | "layout";
export interface PdfPreviewOptions {
  onPageChange?: (page: number, total: number, source: PdfPageChangeSource) => void;
}
export type PdfPreviewHandle = (() => void) & { goToPage(page: number): void };

/** PDF 始终作为数据读取并绘制，避免触发浏览器的文件下载处理。 */
export function mountPdfPreview(
  container: HTMLElement,
  url: string,
  title: string,
  loadLibrary: () => Promise<PdfLibrary> = loadPdfLibrary,
  options: PdfPreviewOptions = {},
): PdfPreviewHandle {
  const viewer = el("div", "pdf-viewer");
  const toolbar = el("div", "pdf-toolbar");
  toolbar.setAttribute("role", "group");
  toolbar.setAttribute("aria-label", "绘本翻页和缩放");
  const makeButton = (label: string) => {
    const button = el("button", "pdf-button", label) as HTMLButtonElement;
    button.type = "button";
    toolbar.appendChild(button);
    return button;
  };
  const prev = makeButton("上一页");
  const position = el("span", "pdf-position", "加载中…");
  position.setAttribute("aria-live", "polite");
  toolbar.appendChild(position);
  const next = makeButton("下一页");
  const smaller = makeButton("缩小");
  const larger = makeButton("放大");
  const fit = makeButton("适合宽度");
  fit.setAttribute("aria-label", "切换整页与适合宽度");
  const viewport = el("div", "pdf-viewport");
  const status = el("p", "hint", "正在加载绘本…");
  status.setAttribute("role", "status");
  const canvas = document.createElement("canvas");
  canvas.className = "pdf-canvas";
  canvas.setAttribute("role", "img");
  canvas.hidden = true;
  viewport.append(status, canvas);
  viewer.append(toolbar, viewport);
  container.appendChild(viewer);

  let disposed = false;
  let successor: PdfPreviewHandle | undefined;
  let documentProxy: PDFDocumentProxy | undefined;
  let loadingTask: PDFDocumentLoadingTask | undefined;
  let renderTask: RenderTask | undefined;
  let pageNumber = 1;
  let zoom = 1;
  let fitMode: "page" | "width" = "page";
  let sequence = 0;
  let busy = true;
  let resizeTimer: ReturnType<typeof setTimeout> | undefined;
  function updateControls() {
    prev.disabled = busy || !documentProxy || pageNumber <= 1;
    next.disabled = busy || !documentProxy || pageNumber >= documentProxy.numPages;
    smaller.disabled = busy || !documentProxy || zoom <= 0.5;
    larger.disabled = busy || !documentProxy || zoom >= 2.5;
    fit.disabled = busy || !documentProxy;
    fit.textContent = fitMode === "page" ? "适合宽度" : "显示整页";
    if (documentProxy) position.textContent = `${pageNumber} / ${documentProxy.numPages} 页 · ${Math.round(zoom * 100)}%`;
  }
  async function render(source: PdfPageChangeSource = "layout") {
    if (!documentProxy || disposed) return;
    const current = ++sequence;
    busy = true;
    updateControls();
    const previous = renderTask;
    previous?.cancel();
    try {
      // 同一个 canvas 上不能并发 render；先等待上一次取消完成。
      if (previous) await previous.promise.catch(() => {});
      if (disposed || current !== sequence) return;
      const page = await documentProxy.getPage(pageNumber);
      if (disposed || current !== sequence) return;
      const natural = page.getViewport({ scale: 1 });
      const padding = window.getComputedStyle(viewport);
      const inset = (value: string) => parseFloat(value) || 0;
      const width = Math.max(1, (viewport.clientWidth || 320) - inset(padding.paddingLeft) - inset(padding.paddingRight));
      const height = Math.max(1, (viewport.clientHeight || 480) - inset(padding.paddingTop) - inset(padding.paddingBottom));
      const widthScale = width / natural.width;
      const baseScale = fitMode === "page" ? Math.min(widthScale, height / natural.height) : widthScale;
      const scale = baseScale * zoom;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const pageViewport = page.getViewport({ scale });
      canvas.width = Math.floor(pageViewport.width * pixelRatio);
      canvas.height = Math.floor(pageViewport.height * pixelRatio);
      canvas.style.width = `${pageViewport.width}px`;
      canvas.style.height = `${pageViewport.height}px`;
      canvas.setAttribute("aria-label", `${title}，第 ${pageNumber} 页，共 ${documentProxy.numPages} 页`);
      canvas.hidden = true;
      status.hidden = false;
      status.textContent = "正在显示页面…";
      renderTask = page.render({ canvas, viewport: pageViewport, transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0] });
      await renderTask.promise;
      if (disposed || current !== sequence) return;
      canvas.hidden = false;
      status.hidden = true;
      options.onPageChange?.(pageNumber, documentProxy.numPages, source);
      viewport.scrollTop = 0;
      viewport.scrollLeft = 0;
    } catch (err) {
      if (disposed || current !== sequence) return;
      if (err instanceof Error && err.name === "RenderingCancelledException") return;
      canvas.hidden = true;
      status.hidden = false;
      status.textContent = "这页暂时无法显示，请切换页面或重新打开绘本。";
    } finally {
      if (!disposed && current === sequence) { busy = false; updateControls(); }
    }
  }
  prev.addEventListener("click", () => { if (!prev.disabled) { pageNumber--; void render("user"); } });
  next.addEventListener("click", () => { if (!next.disabled) { pageNumber++; void render("user"); } });
  smaller.addEventListener("click", () => { if (!smaller.disabled) { zoom = Math.max(0.5, zoom - 0.25); void render(); } });
  larger.addEventListener("click", () => { if (!larger.disabled) { zoom = Math.min(2.5, zoom + 0.25); void render(); } });
  fit.addEventListener("click", () => {
    fitMode = fitMode === "page" ? "width" : "page";
    zoom = 1;
    void render();
  });
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { void render(); }, 120);
  }
  // 实际内容区可能因侧栏、工具栏换行或屏幕旋转改变，不只是 window.resize。
  const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(onResize);
  resizeObserver?.observe(viewport);
  window.addEventListener("resize", onResize);
  const observer = new MutationObserver(() => { if (!viewer.isConnected) dispose(); });
  observer.observe(document.body, { childList: true, subtree: true });
  function dispose() {
    successor?.();
    successor = undefined;
    if (disposed) return;
    disposed = true;
    sequence++;
    clearTimeout(resizeTimer);
    observer.disconnect();
    resizeObserver?.disconnect();
    window.removeEventListener("resize", onResize);
    renderTask?.cancel();
    void loadingTask?.destroy().catch(() => {});
    canvas.width = 0;
    canvas.height = 0;
  }
  updateControls();
  void (async () => {
    try {
      const pdfjs = await loadLibrary();
      if (disposed) return;
      loadingTask = pdfjs.getDocument({
        url,
        cMapUrl: "/assets/pdfjs/cmaps/",
        cMapPacked: true,
        standardFontDataUrl: "/assets/pdfjs/standard_fonts/",
        wasmUrl: "/assets/pdfjs/wasm/",
      });
      documentProxy = await loadingTask.promise;
      if (disposed) return;
      await render("initial");
    } catch {
      if (disposed) return;
      status.textContent = "绘本加载失败，请重试。";
      position.textContent = "加载失败";
      const retry = el("button", "pdf-button", "重试") as HTMLButtonElement;
      retry.addEventListener("click", () => {
        dispose();
        viewer.remove();
        successor = mountPdfPreview(container, url, title, loadLibrary, options);
      });
      viewer.appendChild(retry);
    }
  })();
  return Object.assign(dispose, {
    goToPage(page: number) {
      if (successor) { successor.goToPage(page); return; }
      if (disposed || !documentProxy || !Number.isInteger(page) || page < 1 || page > documentProxy.numPages) return;
      pageNumber = page;
      void render("programmatic");
    },
  });
}
